// LeadRelationshipMap — Phase 6.
//
// Renders the lead's known relationships as a compact tree:
// spouse, children, referrer, employer. Data sources are all fields
// the lead already carries — spouse (life profile beneficiaries),
// children_count (life profile), referrer (top-level lead field),
// employer (top-level notes field). No new tables.
//
// The value here isn't the visual tree per se — it's making the
// cross-sell + referral opportunities visible in one glance.

import React from 'react';

function extractRelationships(lead) {
  const rels = [];
  const life = lead?.insurance_profile?.life || {};

  if (life.beneficiaries) {
    rels.push({
      icon: '💍', label: 'Spouse / beneficiary', name: life.beneficiaries.split(',')[0],
      kind: 'family', opportunity: 'Family health + life cross-sell',
    });
  }
  const children = Number(life.children_count || 0);
  if (children > 0) {
    rels.push({
      icon: '👶', label: `${children} ${children === 1 ? 'child' : 'children'}`, name: null,
      kind: 'family', opportunity: 'Whole-life juvenile policies',
    });
  }

  if (lead?.referrer || lead?.referredBy) {
    rels.push({
      icon: '🤝', label: 'Referred by', name: lead.referrer || lead.referredBy,
      kind: 'referrer', opportunity: 'Thank the referrer — they may know others',
    });
  }

  const notes = String(lead?.notes || '');
  const employerMatch = notes.match(/(?:works at|employer|company)[:\s]+([^,\n]{3,50})/i);
  if (employerMatch) {
    rels.push({
      icon: '🏢', label: 'Employer', name: employerMatch[1].trim(),
      kind: 'work', opportunity: 'Group health + voluntary benefits (COI opening)',
    });
  }
  const churchMatch = notes.match(/(?:church|congregation|faith community)[:\s]+([^,\n]{3,50})/i);
  if (churchMatch) {
    rels.push({
      icon: '⛪', label: 'Church', name: churchMatch[1].trim(),
      kind: 'community', opportunity: 'Community-of-influence referrals',
    });
  }
  const gymMatch = notes.match(/(?:gym|club)[:\s]+([^,\n]{3,50})/i);
  if (gymMatch) {
    rels.push({
      icon: '💪', label: 'Community', name: gymMatch[1].trim(),
      kind: 'community', opportunity: 'Wellness / lifestyle demographic',
    });
  }

  return rels;
}

export default function LeadRelationshipMap({ lead }) {
  const rels = extractRelationships(lead);
  if (rels.length === 0) return null;

  return (
    <div style={{
      background: '#f5f3ff',
      border: '1px solid #ddd6fe',
      borderRadius: 10, padding: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 10 }}>
        <div style={{ fontSize: 10, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#6d28d9', fontWeight: 800 }}>
          Relationship Map
        </div>
        <div style={{ fontSize: 11, color: '#5b21b6' }}>
          {rels.length} connection{rels.length === 1 ? '' : 's'} → cross-sell + referral surface
        </div>
      </div>

      {/* Central lead node + spokes */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        background: '#ffffff', border: '1px solid #ddd6fe',
        borderRadius: 8, padding: 10, marginBottom: 8,
      }}>
        <div style={{
          width: 32, height: 32, borderRadius: 999,
          background: '#7c3aed', color: '#ffffff',
          display: 'grid', placeItems: 'center', fontWeight: 800, fontSize: 12,
        }}>
          {String(lead?.name || '?').split(' ').slice(0, 2).map(w => w[0]?.toUpperCase() || '').join('') || '?'}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: '#0f172a' }}>{lead?.name || '(unnamed)'}</div>
          <div style={{ fontSize: 10, color: '#64748b' }}>the lead</div>
        </div>
      </div>

      <div style={{ display: 'grid', gap: 4 }}>
        {rels.map((r, i) => (
          <div key={i} style={{
            display: 'flex', alignItems: 'center', gap: 10,
            background: '#ffffff', border: '1px solid #ddd6fe',
            borderRadius: 6, padding: '8px 12px',
            marginLeft: 20, position: 'relative',
          }}>
            <span style={{
              position: 'absolute', left: -20, top: 0, bottom: 0, width: 20,
              borderLeft: '2px dashed #c4b5fd',
              borderBottom: i === rels.length - 1 ? '2px dashed #c4b5fd' : 'none',
              borderBottomLeftRadius: i === rels.length - 1 ? 8 : 0,
            }}/>
            <div style={{ fontSize: 18 }}>{r.icon}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, color: '#0f172a', fontWeight: 700 }}>
                {r.label}{r.name && ` — ${r.name}`}
              </div>
              <div style={{ fontSize: 10, color: '#7c3aed', marginTop: 1, fontStyle: 'italic' }}>
                {r.opportunity}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
